"use client";

import { DownloadState, Video } from "@/type/video";
import { Icons } from "./Icons";
import { ProgressRing } from "./ProgressRing";

export const VideoItem = ({
  video,
  index,
  isSelected,
  downloadState,
  onSelect,
  onDownload,
}: {
  video: Video;
  index: number;
  isSelected: boolean;
  downloadState: DownloadState;
  onSelect: () => void;
  onDownload: () => void;
}) => {
  const { progress, isDownloading, isComplete } = downloadState;
  const PAGE_SIZE = 6;

  function formatDuration(millisecond: number): string {
    const sec = Math.floor(millisecond / 1000);
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;

    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  return (
    <div
      className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md hover:border-gray-200 transition-all duration-200 animate-fadeIn"
      style={{ animationDelay: `${(index % PAGE_SIZE) * 80}ms` }}
    >
      <div className="flex items-center gap-4">
        <button
          role="checkbox"
          aria-checked={isSelected}
          aria-label={"选择视频"}
          onClick={onSelect}
          className={`
              w-5 h-5 rounded flex-shrink-0 flex items-center justify-center
              border-2 transition-all duration-200 cursor-pointer
              ${
                isSelected
                  ? "bg-[#2563eb] border-[#2563eb]"
                  : "border-gray-300 hover:border-[#2563eb]"
              }
            `}
        >
          {isSelected && Icons.check("w-3 h-3 text-white")}
        </button>

        {/* 封面 */}
        <div className="relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 group">
          <img
            src={video.cover}
            alt={video.text}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {video.resourceType === "V" && (
            <span className="absolute bottom-1 right-1 bg-black/70 backdrop-blur-sm text-white text-xs px-1.5 py-0.5 rounded">
              {formatDuration(video.duration)}
            </span>
          )}
          {video.resourceType === "P" && (
            <span className="absolute bottom-1 right-1 bg-black/70 backdrop-blur-sm text-white text-xs px-1.5 py-0.5 rounded flex gap-0.5">
              {Icons.image("w-3.5 h-3.5")} {video.image.length}
            </span>
          )}
        </div>

        {/* 信息 */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">
            {video.text}
          </h3>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            {/* <span>{video.duration > 0 ? "视频" : "图片"}</span> */}
            <span className="flex items-center gap-1">
              {Icons.eye("w-3.5 h-3.5")}
              {video.likeCnt}
            </span>
            <span className="flex items-center gap-1">
              {Icons.calendar("w-3.5 h-3.5")}
              {new Date(video.pubTime * 1000).toISOString().slice(0, 10)}
            </span>
          </div>
        </div>

        {/* 下载按钮 */}
        <button
          onClick={onDownload}
          disabled={isDownloading}
          aria-label="下载视频"
          className={`
            relative flex items-center justify-center w-10 h-10 rounded-full 
            transition-all duration-200 flex-shrink-0
            ${
              isComplete
                ? "bg-green-50 text-green-500"
                : isDownloading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-500"
            }
          `}
        >
          {!isDownloading && !isComplete && Icons.download("w-5 h-5")}

          {isDownloading && (
            <>
              <ProgressRing progress={progress} />
              <span className="absolute text-xs font-medium text-red-500">
                {Math.round(progress)}%
              </span>
            </>
          )}

          {isComplete && Icons.check("w-5 h-5")}
        </button>
      </div>
    </div>
  );
};
